import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useCompanySubscription from './queries/useCompanySubscription'
import useCreateBillingSession from './mutations/useCreateBillingSession'
import useUnsubscribe from './mutations/useUnsubscribe'
import useProducts from './queries/useProducts'
import useProductByPriceId from './queries/useProductByPriceId'
import { Price, Product } from '../models/Product'
import type { PlanChangeRequest } from '../components/Pricing/ProductCards'

type UseSubscribeParams = {
	successUrl: string
	cancelUrl: string
	onSuccess?: () => void
}

const useSubscribe = ({ successUrl, cancelUrl, onSuccess }: UseSubscribeParams) => {
	const { products } = useProducts()

	const [enableSeatDialogOpen, setEnableSeatDialogOpen] = useState(false)
	const [pendingSubscribePriceId, setPendingSubscribePriceId] = useState<string | null>(null)
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
	const [pendingPlanChange, setPendingPlanChange] = useState<PlanChangeRequest | null>(null)
	const [pendingCancelSubscriptionId, setPendingCancelSubscriptionId] = useState<string | null>(null)

	const initialCancelAt = useRef<Date | null>(null)
	const initialPriceId = useRef<string | null>(null)

	const { companySubscription, isTrialUsed, shouldShowLoading, startPolling, stopPolling } = useCompanySubscription()

	const isArchivedSubscription = useMemo(() => {
		return (
			companySubscription?.priceId &&
			!products?.some(product => product.prices.some(price => price.id === companySubscription.priceId))
		)
	}, [products, companySubscription])

	const { product: archivedProduct } = useProductByPriceId(
		isArchivedSubscription ? (companySubscription?.priceId ?? null) : null,
	)

	const allProducts = useMemo(() => {
		if (!products) return []
		return archivedProduct ? [...products, archivedProduct] : products
	}, [products, archivedProduct])

	const currentSubscriptionPrice = useMemo((): Price | null => {
		if (!companySubscription?.priceId) return null
		for (const product of allProducts) {
			const price = product.prices.find(p => p.id === companySubscription.priceId)
			if (price) return price
		}
		return null
	}, [companySubscription, allProducts])

	const currentSubscriptionProduct = useMemo((): Product | null => {
		if (!companySubscription?.priceId) return null
		for (const product of allProducts) {
			if (product.prices.some(p => p.id === companySubscription.priceId)) return product
		}
		return null
	}, [companySubscription, allProducts])

	const { handleCreateBillingSession, loading: createLoading } = useCreateBillingSession()
	const { handleUnsubscribe: unsubscribeAsync, loading: unsubscribeLoading } = useUnsubscribe()

	const subscriptionChangeLoading = useMemo(
		() => createLoading || unsubscribeLoading || shouldShowLoading,
		[shouldShowLoading, createLoading, unsubscribeLoading],
	)

	// Track initial subscription state for change detection
	useEffect(() => {
		if (companySubscription && !shouldShowLoading) {
			initialCancelAt.current = companySubscription.cancelAt ?? null
			initialPriceId.current = companySubscription.priceId
		}
	}, [companySubscription, shouldShowLoading])

	const pollStripeHookStatus = useCallback(() => {
		startPolling(1000)
	}, [startPolling])

	const executeSubscribe = useCallback(
		async (priceId: string) => {
			try {
				const response = await handleCreateBillingSession({
					priceId,
					successUrl,
					cancelUrl,
				})
				if (response.url) {
					window.location.href = response.url
				}
				pollStripeHookStatus()
			} catch (error) {
				if (
					error &&
					typeof error === 'object' &&
					'code' in error &&
					(error as { code: string }).code === 'NO_ENABLED_SEATS'
				) {
					setPendingSubscribePriceId(priceId)
					setEnableSeatDialogOpen(true)
				}
			}
		},
		[handleCreateBillingSession, pollStripeHookStatus, successUrl, cancelUrl],
	)

	const executeUnsubscribe = useCallback(
		async (subscriptionId: string) => {
			try {
				await unsubscribeAsync({ subscriptionId })
				pollStripeHookStatus()
			} catch (error) {
				console.error('Failed to unsubscribe:', error)
			}
		},
		[unsubscribeAsync, pollStripeHookStatus],
	)

	const handleSubscribe = useCallback(
		(request: PlanChangeRequest) => {
			const isResubscribingSamePlan =
				companySubscription?.cancelAt && request.priceId === companySubscription.priceId
			// Skip dialog: no subscription, can't resolve current price, or resubscribing to same plan
			if (!companySubscription || !currentSubscriptionPrice || isResubscribingSamePlan) {
				executeSubscribe(request.priceId)
				return
			}
			setPendingPlanChange(request)
			setConfirmDialogOpen(true)
		},
		[companySubscription, currentSubscriptionPrice, executeSubscribe],
	)

	const handleUnsubscribe = useCallback((subscriptionId: string) => {
		setPendingCancelSubscriptionId(subscriptionId)
		setPendingPlanChange(null)
		setConfirmDialogOpen(true)
	}, [])

	const handleConfirmDialogConfirm = useCallback(() => {
		setConfirmDialogOpen(false)
		if (pendingPlanChange) {
			executeSubscribe(pendingPlanChange.priceId)
		} else if (pendingCancelSubscriptionId) {
			executeUnsubscribe(pendingCancelSubscriptionId)
		}
		setPendingPlanChange(null)
		setPendingCancelSubscriptionId(null)
	}, [pendingPlanChange, pendingCancelSubscriptionId, executeSubscribe, executeUnsubscribe])

	const handleConfirmDialogCancel = useCallback(() => {
		setConfirmDialogOpen(false)
		setPendingPlanChange(null)
		setPendingCancelSubscriptionId(null)
	}, [])

	// Detect subscription changes after polling
	useEffect(() => {
		const subscriptionPolledCancelAt = companySubscription?.cancelAt ?? null
		const subscriptionPolledPriceId = companySubscription?.priceId

		const priceIdChanged = initialPriceId.current && subscriptionPolledPriceId !== initialPriceId.current
		const cancelAtChanged = initialCancelAt.current?.getTime() !== subscriptionPolledCancelAt?.getTime()

		if (priceIdChanged || cancelAtChanged) {
			const isResubscribed = cancelAtChanged && !subscriptionPolledCancelAt
			if (priceIdChanged || isResubscribed) {
				onSuccess?.()
			}
			stopPolling()
		}
	}, [onSuccess, companySubscription, stopPolling])

	// Cleanup polling on unmount
	useEffect(() => {
		return () => {
			stopPolling()
		}
	}, [stopPolling])

	const onSeatEnabled = useCallback(async () => {
		if (pendingSubscribePriceId) await executeSubscribe(pendingSubscribePriceId)
	}, [executeSubscribe, pendingSubscribePriceId])

	const dismissEnableSeatDialog = useCallback(() => {
		setEnableSeatDialogOpen(false)
		setPendingSubscribePriceId(null)
	}, [])

	return {
		// Subscription state
		companySubscription,
		isTrialUsed,
		subscriptionChangeLoading,
		currentSubscriptionPrice,
		currentSubscriptionProduct,
		isArchivedSubscription,
		archivedProduct,

		// Actions
		handleSubscribe,
		handleUnsubscribe,

		// Confirm dialog state
		confirmDialogOpen,
		pendingPlanChange,
		pendingCancelSubscriptionId,
		handleConfirmDialogConfirm,
		handleConfirmDialogCancel,

		// Enable seat dialog state
		enableSeatDialogOpen,
		dismissEnableSeatDialog,
		onSeatEnabled,
	}
}

export default useSubscribe
